import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { gmbApi } from '@/services/gmbApi';

function getUserId() {
  return localStorage.getItem('user_id') || '';
}

// ── Implementation Overview ───────────────────────────────────────────

export function useGmbImplementationOverview() {
  const userId = getUserId();
  return useQuery({
    queryKey: ['gmb', 'implementation', 'overview', userId],
    queryFn: () => gmbApi.getImplementationOverview(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Photos ────────────────────────────────────────────────────────────

export function useGmbPhotos() {
  const userId = getUserId();
  return useQuery({
    queryKey: ['gmb', 'implementation', 'photos', userId],
    queryFn: () => gmbApi.getPhotos(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUploadPhoto() {
  const queryClient = useQueryClient();
  const userId = getUserId();

  return useMutation({
    mutationFn: (formData: FormData) => gmbApi.uploadPhoto(userId, formData),
    onSuccess: () => {
      // Governance reads these changes (activity log, publishing status).
      queryClient.invalidateQueries({ queryKey: ['gmb', 'governance'] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'photos', userId] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'videos', userId] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'overview', userId] });
    },
  });
}

export function useDeletePhoto() {
  const queryClient = useQueryClient();
  const userId = getUserId();

  return useMutation({
    mutationFn: (photoId: string) => gmbApi.deletePhoto(userId, photoId),
    onSuccess: () => {
      // Governance reads these changes (activity log, publishing status).
      queryClient.invalidateQueries({ queryKey: ['gmb', 'governance'] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'photos', userId] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'videos', userId] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'overview', userId] });
    },
  });
}

// ── Videos ────────────────────────────────────────────────────────────

export function useGmbVideos() {
  const userId = getUserId();
  return useQuery({
    queryKey: ['gmb', 'implementation', 'videos', userId],
    queryFn: () => gmbApi.getVideos(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUploadVideo() {
  const queryClient = useQueryClient();
  const userId = getUserId();

  return useMutation({
    mutationFn: (formData: FormData) => gmbApi.uploadVideo(userId, formData),
    onSuccess: () => {
      // Governance reads these changes (activity log, publishing status).
      queryClient.invalidateQueries({ queryKey: ['gmb', 'governance'] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'videos', userId] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'photos', userId] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'overview', userId] });
    },
  });
}

// ── Posts ─────────────────────────────────────────────────────────────

export function useGmbPosts() {
  const userId = getUserId();
  return useQuery({
    queryKey: ['gmb', 'implementation', 'posts', userId],
    queryFn: () => gmbApi.getPosts(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdatePost() {
  const queryClient = useQueryClient();
  const userId = getUserId();
  const postsKey = ['gmb', 'implementation', 'posts', userId];
  const contentPlanKey = ['gmb', 'strategy', 'action-plan', 'content', userId];

  return useMutation({
    mutationFn: ({ postId, data }: { postId: string; data: any }) =>
      gmbApi.updatePost(userId, postId, data),
    onSuccess: (updated, variables) => {
      // Governance reads these changes (activity log, publishing status).
      queryClient.invalidateQueries({ queryKey: ['gmb', 'governance'] });
      queryClient.setQueryData(postsKey, (old: any) => {
        if (!old?.posts) return old;
        const u = updated ?? {};
        return {
          ...old,
          posts: old.posts.map((p: any) =>
            p.id === variables.postId
              ? {
                  ...p,
                  title: u.title ?? p.title,
                  caption: u.caption ?? p.caption,
                  image_url: u.image_url ?? p.image_url,
                  image_description: u.image_description ?? p.image_description,
                  status: u.status ?? p.status,
                  keyword_used: u.keyword_used ?? p.keyword_used,
                  scheduled_date: u.scheduled_date ?? p.scheduled_date,
                }
              : p
          ),
        };
      });
      queryClient.invalidateQueries({ queryKey: postsKey });
      queryClient.invalidateQueries({ queryKey: contentPlanKey });
    },
  });
}

export function useGeneratePostImageDescription() {
  const queryClient = useQueryClient();
  const userId = getUserId();
  const postsKey = ['gmb', 'implementation', 'posts', userId];

  return useMutation({
    mutationFn: ({ postId, imageUrl }: { postId: string; imageUrl?: string }) =>
      gmbApi.generatePostImageDescription(userId, postId, imageUrl),
    onSuccess: () => {
      // Governance reads these changes (activity log, publishing status).
      queryClient.invalidateQueries({ queryKey: ['gmb', 'governance'] });
      queryClient.invalidateQueries({ queryKey: postsKey });
      queryClient.invalidateQueries({
        queryKey: ['gmb', 'strategy', 'action-plan', 'content', userId],
      });
    },
  });
}

export function usePublishPost() {
  const queryClient = useQueryClient();
  const userId = getUserId();

  return useMutation({
    mutationFn: (postId: string) => gmbApi.publishPost(userId, postId),
    onSuccess: () => {
      // Governance reads these changes (activity log, publishing status).
      queryClient.invalidateQueries({ queryKey: ['gmb', 'governance'] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'posts', userId] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'overview', userId] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'strategy', 'action-plan', 'content', userId] });
    },
  });
}

export function useSchedulePost() {
  const queryClient = useQueryClient();
  const userId = getUserId();

  return useMutation({
    mutationFn: (postId: string) => gmbApi.schedulePost(userId, postId),
    onSuccess: () => {
      // Governance reads these changes (activity log, publishing status).
      queryClient.invalidateQueries({ queryKey: ['gmb', 'governance'] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'posts', userId] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'overview', userId] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'strategy', 'action-plan', 'content', userId] });
    },
  });
}

export function useEditPostImage() {
  const queryClient = useQueryClient();
  const userId = getUserId();
  const postsKey = ['gmb', 'implementation', 'posts', userId];

  return useMutation({
    mutationFn: ({ postId, prompt, imageUrl }: { postId: string; prompt: string; imageUrl?: string }) =>
      gmbApi.editPostImage(userId, postId, prompt, imageUrl),
    onSuccess: (updated, variables) => {
      // Governance reads these changes (activity log, publishing status).
      queryClient.invalidateQueries({ queryKey: ['gmb', 'governance'] });
      queryClient.setQueryData(postsKey, (old: any) => {
        if (!old?.posts) return old;
        const u = updated ?? {};
        return {
          ...old,
          posts: old.posts.map((p: any) =>
            p.id === variables.postId
              ? { ...p, image_url: u.image_url ?? p.image_url }
              : p
          ),
        };
      });
      queryClient.invalidateQueries({ queryKey: postsKey });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'strategy', 'action-plan', 'content', userId] });
    },
  });
}

// ── Reviews ───────────────────────────────────────────────────────────

export function useGmbReviews() {
  const userId = getUserId();
  return useQuery({
    queryKey: ['gmb', 'implementation', 'reviews', userId],
    queryFn: () => gmbApi.getReviews(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useGmbReviewsOverview() {
  const userId = getUserId();
  return useQuery({
    queryKey: ['gmb', 'implementation', 'reviews', 'overview', userId],
    queryFn: () => gmbApi.getReviewsOverview(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useGenerateReviewResponse() {
  const userId = getUserId();

  return useMutation({
    mutationFn: (reviewId: string) => gmbApi.generateReviewResponse(userId, reviewId),
  });
}

export function useMarkReviewResponded() {
  const queryClient = useQueryClient();
  const userId = getUserId();

  return useMutation({
    mutationFn: ({ reviewId, responseText }: { reviewId: string; responseText: string }) =>
      gmbApi.markReviewResponded(userId, reviewId, responseText),
    onMutate: async ({ reviewId, responseText }) => {
      const reviewsKey = ['gmb', 'implementation', 'reviews', userId];
      await queryClient.cancelQueries({ queryKey: reviewsKey });
      const prev = queryClient.getQueryData(reviewsKey);
      queryClient.setQueryData(reviewsKey, (old: any) => {
        if (!old?.reviews) return old;
        return {
          ...old,
          reviews: old.reviews.map((r: any) =>
            r.id === reviewId
              ? { ...r, response_status: 'responded', response_text: responseText || r.response_text }
              : r,
          ),
        };
      });
      return { prev, reviewsKey };
    },
    onError: (_err, _vars, ctx: any) => {
      if (ctx?.prev) {
        queryClient.setQueryData(ctx.reviewsKey, ctx.prev);
      }
    },
    onSuccess: () => {
      // Governance reads these changes (activity log, publishing status).
      queryClient.invalidateQueries({ queryKey: ['gmb', 'governance'] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'reviews', userId] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'reviews', 'overview', userId] });
    },
  });
}

// ── Q&A ───────────────────────────────────────────────────────────────

export function useGmbQaItems() {
  const userId = getUserId();
  return useQuery({
    queryKey: ['gmb', 'implementation', 'qa', userId],
    queryFn: () => gmbApi.getQAItems(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSubmitQAAnswer() {
  const queryClient = useQueryClient();
  const userId = getUserId();

  return useMutation({
    mutationFn: ({ qaId, answerText }: { qaId: string; answerText: string }) =>
      gmbApi.submitQAAnswer(userId, qaId, answerText),
    onSuccess: () => {
      // Governance reads these changes (activity log, publishing status).
      queryClient.invalidateQueries({ queryKey: ['gmb', 'governance'] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'qa', userId] });
    },
  });
}

export function useFlagQAItem() {
  const queryClient = useQueryClient();
  const userId = getUserId();

  return useMutation({
    mutationFn: (qaId: string) => gmbApi.flagQAItem(userId, qaId),
    onSuccess: () => {
      // Governance reads these changes (activity log, publishing status).
      queryClient.invalidateQueries({ queryKey: ['gmb', 'governance'] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'qa', userId] });
    },
  });
}

// ── Checklist ─────────────────────────────────────────────────────────

export function useGmbChecklist() {
  const userId = getUserId();
  return useQuery({
    queryKey: ['gmb', 'implementation', 'checklist', userId],
    queryFn: () => gmbApi.getChecklist(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}
